<template>
  <h6
    class="feelingHeadingDv"
    v-if="post && (post.feeling || post.userLocation)"
  >
    <div class="profile_feeling">
      <div class="feeling c-pointer" v-if="post.feeling">
        <span class="headingStart">{{ $t("common.common.is_feeling") }}</span>
        <IconComponent
          path="/feelings"
          className="infoLeftDvIconInfo"
          :icon="`${post.feeling}.svg`"
        />
        <span class="nextinfoLeftInfoDv"> {{ post.feeling }}</span>
      </div>
      <div
        class="feeling c-pointer"
        v-if="post.userLocation && Object.keys(post.userLocation).length"
      >
        <span class="headingStart" style="margin-inline: 7px">{{
          $t("common.common.in")
        }}</span>
        <span class="nextinfoLeftInfoDv">
          {{ post.userLocation?.displayName }}</span
        >
      </div>
    </div>
  </h6>
  <div class="text_post" v-if="post.body && post.body !== '.'">
    <div
      ref="textElement"
      @click="handleTextElementClicked"
      class="post_message description_text"
      :class="post._id"
      :innerHTML="post.body?.replace('target=', '')"
    ></div>
    <span
      v-if="isTruncated"
      class="anchor_tag--more"
      @click.stop="checkTextLength(false)"
    >
      {{ $t("common.common.see_more") }}
    </span>
  </div>
  <div
    class="text_post"
    v-if="showQuotedBlock"
    :class="{ quotedText: post.quotedImage, detailQuoted: isDetailView }"
    :style="getStyle()"
  >
    <div
      ref="quotedTextElement"
      @click="handleTextElementClicked"
      class="post_message description_text"
      :class="[post._id, { expanded: isDetailView }]"
      :innerHTML="quotedDisplayText"
    ></div>
    <span
      v-if="isQuotedTruncated && !isDetailView"
      class="anchor_tag--more"
      @click.stop="checkQuotedTextLength(false)"
    >
      {{ $t("common.common.see_more") }}
    </span>
  </div>
</template>

<script lang="ts">
import PostsModel, { PostSourceType } from "../../models/posts.model";
import utilService from "@/services/util.service";
import { computed, defineComponent, nextTick, onMounted, ref, watch } from "vue";
import { usePosts } from "../../composables/posts.composables";
import { useRouter } from "vue-router";
type HashTag = { name: string; value: string; isActive: boolean };
type Stock = { symbol: string; stockId: string };

export default defineComponent({
  props: ["post", "isDetailView", "hideQuoted"],
  emits: ["content-rendered", "chart-loaded", "media-loaded"],

  components: {},
  setup(props, { emit }) {
    const post = ref<PostsModel>(props.post as PostsModel);
    const isTruncated = ref<boolean>(false);
    const isQuotedTruncated = ref<boolean>(false);
    const textElement = ref<any>();
    const quotedTextElement = ref<any>();

    const showQuotedBlock = computed(
      () => !!(props.post as PostsModel)?.quotedImage && !props.hideQuoted
    );

    const quotedDisplayText = computed(() => {
      const p = props.post as PostsModel;
      if (!p) return "";
      const quoted = (p.quotedText || "").toString().trim();
      if (quoted && quoted !== ".") return p.quotedText;
      const body = (p.body || "").toString().trim();
      if (body && body !== ".") return p.body;
      return p.quotedText || "";
    });

    watch(
      () => props.post,
      (val) => {
        if (val) post.value = val as PostsModel;
      },
      { deep: true }
    );

    const postBodyContent = computed(() => {
      let content = post.value.body;
      // Remove <div><br>…</div>
      content = content.replace(
        /(?:<div>\s*<br>\s*<\/div>\s*|\s*<br>\s*)+$/gi,
        ""
      );

      content = content?.replace("target=", "");

      return content;
    });
    onMounted(() => {
      post.value.body = post.value.body!.replaceAll(
        '<a href="',
        '<a target="_blank" href="'
      );
      if (
        (post.value.sourceType == PostSourceType.TWITTERTWEET ||
          post.value.sourceType == PostSourceType.DATATEAM) &&
        post.value.links.length
      ) {
        if (!post.value.body.includes(post.value.links[0])) {
          var displayText: string = JSON.parse(
            JSON.stringify(post.value.links[0])
          );
          displayText = displayText.replaceAll("https://", "");
          displayText = displayText.replaceAll("http://", "");
          post.value.body += `<br /><a style="text-transform: lowercase !important;" class="anchor_tag--more" href="${post.value.links[0]}" target="_blank"> ${displayText} </a>`;
        }
      }
      checkTextLength(true);
      checkQuotedTextLength(true);
      nextTick(() => {
        emit("content-rendered");
      });
    });

    const checkTextLength = (toTruncate = true) => {
      const el = textElement.value;
      if (!el) return;

      if (toTruncate) {
        el.classList.remove("expanded");
      } else {
        el.classList.add("expanded");
      }

      // overflow check AFTER clamp
      const isOverflowing = el.scrollHeight > el.clientHeight + 2;

      isTruncated.value = toTruncate && isOverflowing;
    };

    const checkQuotedTextLength = (toTruncate = true) => {
      const el = quotedTextElement.value;
      if (!el) return;

      if (toTruncate) {
        el.classList.remove("expanded");
      } else {
        el.classList.add("expanded");
      }

      const isOverflowing = el.scrollHeight > el.clientHeight + 2;
      isQuotedTruncated.value = toTruncate && isOverflowing;
    };

    const getStyle = () => {
      var style: any = {};
      const currentPost = props.post as PostsModel;
      if (currentPost?.quotedImage) {
        style.background = `url(${require("@/assets/images/quoted_text/quoted_" +
          currentPost.quotedImage +
          ".png")}) center center/cover no-repeat`;
      }
      return style;
    };

    const {
      hashTaglist,
      applyFilter,
      postTypelist,
      scopelist,
      stockData,
      checkIfItemAlreadySelected,
      advanceFiltering,
      clearFilter,
      selectedAdvanceFilter,
      getStocks,
      isFromHighlight,
      loading,
      selectAdvanceFilter,
      Advancefilters,
    } = usePosts();

    function formatHashtag(raw: string): HashTag {
      const word = raw.toLowerCase().replace("#", "").trim();
      const formattedName = "# " + word.charAt(0).toUpperCase() + word.slice(1);
      return {
        name: formattedName,
        value: `HT_${word.toUpperCase()}`,
        isActive: true,
      };
    }

    function formatStock(raw: string): Stock {
      const symbol = raw.toLowerCase().replace("$", "").trim();
      const finalSymbol = symbol.charAt(0).toUpperCase() + symbol.slice(1);
      return {
        symbol: finalSymbol,
        stockId: `"${symbol.toUpperCase()}_NASDAQ"`,
      };
    }

    function handleHashtag(text: string) {
      const clean = text.replace(/\s+/g, "").toLowerCase();
      const exists = hashTaglist.value.some(
        (h) => h.name.replace(/\s+/g, "").toLowerCase() === clean
      );
      if (!exists) {
        const tag = formatHashtag(clean);
        hashTaglist.value.push(tag);
        advanceFiltering(tag.value);
      } else {
        const existing = hashTaglist.value.find(
          (h) => h.name.replace(/\s+/g, "").toLowerCase() === clean
        );
        if (existing) {
          advanceFiltering(existing.value.toUpperCase());
        }
      }
    }

    function handleStock(text: string) {
      const clean = text.replace(/\s+/g, "").toLowerCase().replace("$", "");
      const exists = stockData.value.some(
        (s) => s.symbol.toLowerCase() === clean.toLowerCase()
      );

      if (!exists) {
        const stock = formatStock(text);
        stockData.value.push(stock);
        advanceFiltering(`TK_${clean.toUpperCase()}`);
      } else {
        advanceFiltering(`TK_${clean.toUpperCase()}`);
      }
    }
    const router = useRouter();

    const handleTextElementClicked = (event) => {
      const target = event.target as HTMLElement | any;
      if (target.tagName.toLowerCase() === "a") {
        event.preventDefault();
        if (target.classList.contains("anchor_tag--user")) {
          const href = target.getAttribute("href");
          if (href) {
            window.scrollTo(0, 0);
            router.push(href);
          }
          return;
        }
        const text = target.textContent?.trim() || "";
        if (target.classList.contains("anchor_tag--cash")) {
          const stockId = target.id?.replace(/^\$/, "") || "";
          handleStock(stockId);
        }
        if (target.classList.contains("anchor_tag")) {
          const href = target.getAttribute("href") || "";
          if (href.startsWith("http://") || href.startsWith("https://")) {
            window.open(href, "_blank");
            return;
          }
          const text = target.textContent?.trim() || "";
          handleHashtag(text);
          return;
        }
        return;
      }
      return !isTruncated.value ? checkTextLength(event) : null;
    };

    return {
      post,
      getStyle,
      isTruncated,
      isQuotedTruncated,
      handleTextElementClicked,
      textElement,
      quotedTextElement,
      checkTextLength,
      checkQuotedTextLength,
      showQuotedBlock,
      quotedDisplayText,
      isDetailView: computed(() => props.isDetailView),
    };
  },
});
</script>
<style lang="scss" scoped>
.feelingHeadingDv {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 0px 0px 10px;
  padding: 0px;

  .profile_feeling {
    display: flex;
    align-items: center;
  }

  .feeling {
    margin-inline-start: 0px;
    color: var(--base-text-white);
    font-size: var(--font-xs);
    font-weight: 400;
    line-height: 1;
    margin: 0px;
    padding: 0px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .nextinfoLeftInfoDv {
    margin: -5px 0px 0px;
    display: block;
  }

  .headingStart {
    color: #8e8c97;
    font-size: var(--font-xs);
    font-weight: 400;
    line-height: 1;
    margin: -5px 0px 0px;
    padding: 0px;
    display: block;
  }
}

@media only screen and (min-width: 1280px) and (max-width: 1891px) {
  .feelingHeadingDv {
    .profile_feeling {
      font-size: var(--font-2xs);
    }

    .headingStart {
      font-size: var(--font-2xs);
    }

    .nextinfoLeftInfoDv {
      font-size: var(--font-2xs);
    }

    .feeling {
      font-size: var(--font-2xs);
    }
  }
}

.description_text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;

  /* 👇 number of lines you want */
  -webkit-line-clamp: 4;

  line-height: 1.5;
  max-height: none;
}
.description_text.expanded {
  -webkit-line-clamp: unset;
  max-height: none;
  overflow: visible;
}

/* Quoted styles for contexts outside .card--post (e.g. detail modal) */
.text_post.quotedText {
  vertical-align: middle;
  display: flex;
  align-items: center;
  justify-content: center;
  word-break: break-word;
  text-align: center;
  padding: 10px;
  border-radius: 12px;
  color: #fff;
  min-height: 180px;
  overflow: auto;

  .post_message {
    font-size: var(--font-3xl);
    font-family: Georgia, "Times New Roman", Times, serif;
    font-weight: 700;
    color: #fff;
  }

  &.detailQuoted {
    min-height: 280px;
    width: 100%;
  }
}
</style>
